import { useState } from "react";
import { useAuth } from "@/shared/hooks";
import type { Locale } from "@/shared/utils";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { MembersSection, TaskCalendar } from "@/shared/components";
import { MembersModal } from "@/features/projects";
import type { Assignee, BoardProject, TaskListItem } from "../../../types/api";
import TaskFormModal from "./TaskFormModal";
import DescriptionSection from "./DescriptionSection";
import GoalsSection from "./GoalsSection";
import ProjectOverviewSidebar from "./ProjectOverviewSidebar";

interface Props {
  project: BoardProject;
  assignees: Assignee[];
  allTasks: TaskListItem[];
  kpiTasks: TaskListItem[];
  locale: Locale;
  onTaskClick: (task: TaskListItem, projectId: number) => void;
}

export default function ProjectWorkspace({ project, assignees, allTasks, kpiTasks, locale, onTaskClick }: Props) {
  const { hasPermission } = useAuth();

  const canEdit = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);
  const canManage = hasPermission(PERMISSIONS.PROJECTS.MANAGE_TASKS);

  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showKpiForm, setShowKpiForm] = useState(false);

  function handleCreateKpi() {
    setShowKpiForm(true);
  }

  return (
    <>
      <div className="flex gap-6">
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          <DescriptionSection projectId={project.id} description={project.description} canEdit={canEdit} />
          <MembersSection
            members={assignees}
            canEdit={canEdit}
            onOpenModal={() => setShowMembersModal(true)}
            locale={locale}
          />
          <GoalsSection
            allTasks={allTasks}
            canManage={canManage}
            onCreateKpi={handleCreateKpi}
            onKpiClick={onTaskClick}
            projectId={project.id}
          />
          <TaskCalendar
            tasks={allTasks}
            locale={locale}
            onTaskClick={(task) => onTaskClick(task as TaskListItem, project.id)}
          />
        </div>

        <div className="w-100 shrink-0">
          <ProjectOverviewSidebar project={project} locale={locale} />
        </div>
      </div>

      {showMembersModal && <MembersModal projectId={project.id} onClose={() => setShowMembersModal(false)} />}

      {showKpiForm && (
        <TaskFormModal
          onClose={() => setShowKpiForm(false)}
          onSuccess={() => setShowKpiForm(false)}
          isRefetching={false}
          defaultStatus={null}
          mode="create"
          assignees={assignees}
          projectId={project.id}
          kpiTasks={kpiTasks}
          forceKpi
        />
      )}
    </>
  );
}

